/**
 * Where the mail composer's window sits on screen.
 *
 * Moved out of `Composer.tsx` unchanged. It is pure arithmetic over the viewport
 * size and the window state, so it needs neither React nor the component.
 */
import { NAV_DRAWER_MAX } from "../../shell/navDrawer";

export type WindowState = "dock" | "min" | "max";

const GUTTER = 22;
const DOCK_W = 466;
const DOCK_H = 560;

/**
 * Where the window sits in each of its three states.
 *
 * The box is animated directly rather than through framer's `layout` prop: a
 * layout animation gets from one box to the other by scaling, and a window full
 * of type at 0.5x scale for 380ms reads as a rendering fault rather than as a
 * resize. Animating width and height means the text reflows honestly the whole
 * way across.
 *
 * On a phone there is only one honest state and it is full screen. The docked
 * box is 466px wide by 560px tall, which on a 390px screen clamps to a 346px
 * card floating over the pane behind it: 22px of margin down both sides of the
 * only text field that matters, and a "keep reading the thread underneath"
 * trade that a phone cannot pay for because there is no underneath. Collapsing
 * still works - it is how you put a half-written reply down to go and check
 * something - and it leaves the title bar across the bottom of the screen.
 */
export function geometry(state: WindowState, vw: number, vh: number) {
  if (vw <= NAV_DRAWER_MAX) {
    if (state === "min") return { width: vw, height: 54, right: 0, bottom: 0, corners: "20px 20px 0px 0px" };
    return { width: vw, height: vh, right: 0, bottom: 0, corners: "0px" };
  }
  if (state === "max") {
    const w = Math.min(900, vw - 48);
    const h = Math.min(760, vh - 88);
    return {
      width: w,
      height: h,
      right: Math.round((vw - w) / 2),
      bottom: Math.round((vh - h) / 2),
      corners: "32px",
    };
  }
  const w = Math.min(DOCK_W, vw - GUTTER * 2);
  const corners = "28px 28px 0px 0px";
  if (state === "min") return { width: w, height: 54, right: GUTTER, bottom: 0, corners };
  return { width: w, height: Math.min(DOCK_H, vh - 96), right: GUTTER, bottom: 0, corners };
}
